import { createContext, useContext, useMemo, useState } from 'react';
import { DEFAULT_USERS, DEFAULT_OPERATING_COSTS } from '../config/defaultData';
import { useAuth } from './AuthContext';

const AppDataContext = createContext(null);

function AppDataStateProvider({ children }) {
  const [bottles, setBottles] = useState({});
  const [skuMap, setSkuMap] = useState({});
  const [wineryMap, setWineryMap] = useState({});
  const [logs, setLogs] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [packageTypes, setPackageTypes] = useState([]);
  const [operatingCosts, setOperatingCosts] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [memos, setMemos] = useState([]);
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [users, setUsers] = useState(DEFAULT_USERS);
  const [pettyCash, setPettyCash] = useState([]);
  const [quizResults, setQuizResults] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [salesRecords, setSalesRecords] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState({});
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [stockCheckRotation, setStockCheckRotation] = useState({ rotation_index: 0, last_date: '' });
  const [stockAdjustmentLog, setStockAdjustmentLog] = useState([]);
  const [meta, setMeta] = useState({});

  const value = useMemo(
    () => ({
      bottles, setBottles,
      skuMap, setSkuMap,
      wineryMap, setWineryMap,
      logs, setLogs,
      shipments, setShipments,
      orders, setOrders,
      customers, setCustomers,
      packageTypes, setPackageTypes,
      operatingCosts, setOperatingCosts,
      distributors, setDistributors,
      tasks, setTasks,
      memos, setMemos,
      showNewProduct, setShowNewProduct,
      users, setUsers,
      pettyCash, setPettyCash,
      quizResults, setQuizResults,
      purchases, setPurchases,
      salesRecords, setSalesRecords,
      invoices, setInvoices,
      suppliers, setSuppliers,
      purchaseOrders, setPurchaseOrders,
      stockCheckRotation, setStockCheckRotation,
      stockAdjustmentLog, setStockAdjustmentLog,
      meta, setMeta,
      DEFAULT_OPERATING_COSTS,
    }),
    [
      bottles, skuMap, wineryMap, logs, shipments, orders, customers,
      packageTypes, operatingCosts, distributors,
      tasks, memos, showNewProduct, users, pettyCash,
      quizResults, purchases, salesRecords, invoices,
      stockCheckRotation, stockAdjustmentLog,
      suppliers, purchaseOrders, meta,
    ]
  );

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function AppDataProvider({ children }) {
  const { user, isLoading } = useAuth();
  const authScopeKey = isLoading ? 'auth-loading' : user?.firebaseUid ?? 'signed-out';

  return <AppDataStateProvider key={authScopeKey}>{children}</AppDataStateProvider>;
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used within AppDataProvider');
  return ctx;
}

// Backward-compatible alias
export const useAppDataContext = useAppData;
